import { Modal } from "bootstrap"
import Sortable from "sortablejs"
import videojs from "video.js"
import { $ } from "../lib/dom"
import { CompletedVideoLesson } from "../lib/json_resource"
import { Emitter, Widget } from "../lib/widget"
import { escapeHtml, Utils } from "../lib/utils"

class VideoUploadView extends Widget {
  constructor(options = {}) {
    super()
    this.fileIdInput = options.fileIdInput
    this.fileNameInput = options.fileNameInput
    this.fileTypeInput = options.fileTypeInput
    this.onSuccess = options.onSuccess
    this.delegate("click", "#upload-video", this.triggerFileDialog)
    this.delegate("change", "#video-file-input", this.verifyFile)
  }

  triggerFileDialog(event) {
    event.preventDefault()
    this.$("#video-file-input").trigger("click")
  }

  verifyFile(_event, input) {
    const file = input.files?.[0]
    if (!file) return
    if (!["video/mp4", "video/quicktime", "video/x-msvideo"].includes(file.type)) {
      return this.showError("Unsupported format. Please choose an mp4, avi, or mov file.")
    }
    if (file.size > 2_147_482_548) return this.showError("File size too large. Please select a video under 2GB.")
    this.uploadVideo()
  }

  showError(message) {
    window.alert(message)
  }

  uploadVideo() {
    const form = document.querySelector("#video-upload-form")
    const input = document.querySelector("#video-file-input")
    const file = input?.files?.[0]
    if (!form || !file) return

    this.$(".video-upload").addClass("processing")
    const xhr = new XMLHttpRequest()
    xhr.open("POST", form.action)
    xhr.setRequestHeader("Accept", "application/json")
    xhr.upload.addEventListener("progress", (event) => {
      if (!event.lengthComputable) return
      const progress = Math.round((event.loaded / event.total) * 100)
      this.$(".video-upload .status").text(progress < 100 ? `Uploading... ${progress}%` : "Finishing up...")
      this.$(".video-upload .progress-bar").css({ width: `${progress}%` })
    })
    xhr.addEventListener("loadend", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const data = JSON.parse(xhr.responseText || "{}")
        $(this.fileIdInput).val(data.Key || "")
        $(this.fileNameInput).val(file.name)
        $(this.fileTypeInput).val(file.type)
        this.$(".video-upload").removeClass("processing").addClass("complete")
        this.onSuccess?.()
      } else {
        this.$(".video-upload").removeClass("processing")
        this.showError("An error occurred while uploading. Please try again.")
      }
    })
    xhr.send(new FormData(form))
  }
}

class ExerciseFormView extends Widget {
  constructor(data = {}) {
    super()
    this.delegate("click", ".add-field", this.addField)
    this.delegate("click", ".add-question", this.addQuestion)
    this.delegate("change", ".question-type", this.updateQuestionFields)
    this.initializeForm(data || {})
  }

  initializeForm(data) {
    this.initializing = true
    this.$('[name="video_lesson[exercise][title]"]').val(data.title)
    this.$('[name="video_lesson[exercise][heading]"]').val(data.heading)
    this.$('[name="video_lesson[exercise][instructions]"]').val(data.instructions)
    this.populateFields(data.choices, this.$('[name="video_lesson[exercise][choices][]"]'))
    ;(data.questions || []).forEach((question) => {
      const row = this.addQuestion()
      row.find('[name="video_lesson[exercise][questions][][type]"]').val(question.type).trigger("change")
      row.find('[name="video_lesson[exercise][questions][][text]"]').val(question.text)
      this.populateFields(question.choices, row.find('[name="video_lesson[exercise][questions][][choices][]"]'))
      row.find('[name="video_lesson[exercise][questions][][choice_labels]"]').prop("checked", question.choice_labels)
      row.find('[name="video_lesson[exercise][questions][][answer]"]').val(question.answer)
    })
    this.initializing = false
  }

  populateFields(data, fields) {
    fields.val("")
    ;(data || []).forEach((value, index) => {
      if (index === 0) $(fields[0]).val(value)
      else this.addField(null, fields[0], value)
    })
  }

  addQuestion(event) {
    event?.preventDefault()
    const number = this.$(".exercise-form .question").length + 1
    return $(`
      <tr class="question">
        <td>Question ${number}</td>
        <td>
          <select name="video_lesson[exercise][questions][][type]" class="form-select question-type">
            <option selected disabled>Select Type...</option>
            <option value="true-false">True/False</option>
            <option value="multiple-choice">Multiple Choice</option>
            <option value="fill-in-the-blank">Fill In The Blank</option>
          </select>
        </td>
      </tr>
    `).insertBefore(this.$(".add-question-row"))
  }

  updateQuestionFields(_event, select) {
    const type = select.value
    const cell = $(select).closest("td")
    cell.find(".question-details").remove()
    cell.append(this.questionDetails(type))
    if (type === "true-false" && !this.initializing) {
      this.populateFields(["True", "False"], cell.find('[name="video_lesson[exercise][questions][][choices][]"]'))
    }
  }

  questionDetails(type) {
    const hasChoices = ["multiple-choice", "true-false"].includes(type)
    return `
      <table class="table table-sm question-details">
        <tr><td>Text</td><td><input name="video_lesson[exercise][questions][][text]" type="text" class="form-control"></td></tr>
        ${hasChoices ? `
          <tr><td>Choices</td><td>
            <div class="input-group">
              <span class="input-group-text">A.</span>
              <input type="text" name="video_lesson[exercise][questions][][choices][]" class="form-control">
              <span class="input-group-action"><button class="btn btn-outline-secondary btn-sm add-field" type="button" tabindex="-1"><span class="bi bi-plus-lg"></span></button></span>
            </div>
          </td></tr>
          <tr class="choice-labels-row"><td></td><td><label><input name="video_lesson[exercise][questions][][choice_labels]" type="checkbox" value="1"> Show Labels</label></td></tr>
        ` : ""}
        <tr><td>Answer</td><td><input type="text" name="video_lesson[exercise][questions][][answer]" class="form-control"></td></tr>
      </table>
    `
  }

  addField(event, source, value) {
    event?.preventDefault()
    const element = $(source || event?.target)
    const cell = element.closest("td")
    const inputGroup = cell.find(".input-group").first().clone()
    const inputNumber = cell.find(".input-group").length + 1
    const label = cell.closest(".question-details").length ? Utils.numberToAlpha(inputNumber) : inputNumber
    inputGroup.find(".input-group-text").text(`${label}.`)
    inputGroup.find("input").removeAttr("placeholder").val(value)
    inputGroup.find(".input-group-action").remove()
    cell.append(inputGroup)
  }
}

class ExerciseView extends Emitter {
  constructor(options = {}) {
    super()
    this.id = options.id
    this.data = options.data
    this.isOpen = false
    this.element = document.createElement("div")
  }

  render() {
    const exercise = this.formattedData() || {}
    const questions = (exercise.questions || []).map((question, qindex) => {
      const choices = (question.choices || []).length ? `
        <ol class="exercise__choice-list${question.choice_labels ? " show-labels" : ""}">
          ${question.choices.map((option, cindex) => `
            <li class="form-check"><label class="form-check-label">
              <input class="form-check-input" type="radio" name="q${qindex + 1}-choices" value="${Utils.numberToAlpha(cindex + 1)}">
              ${escapeHtml(option)}
            </label></li>
          `).join("")}
        </ol>
      ` : ""
      const wordBank = qindex === exercise.wordBankIndex && (exercise.choices || []).length ? `
        <div class="draggable exercise__word-bank">
          ${exercise.choices.map((choice, index) => `<span class="exercise__word" data-cid="${index + 1}">${escapeHtml(choice)}</span>`).join("")}
        </div>
      ` : ""
      return `<div class="exercise__question exercise__question--${escapeHtml(question.type)}">${question.text}</div>${choices}${wordBank}`
    }).join("")

    this.element.innerHTML = `
      <div class="modal fade exercise" id="exercise" tabindex="-1" role="dialog" aria-labelledby="exercise-label">
        <div class="modal-dialog modal-xl" role="document"><div class="modal-content">
          <div class="modal-header">
            <h4 class="modal-title exercise__title" id="exercise-label">${escapeHtml(exercise.title)}</h4>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body exercise__body">
            ${exercise.heading ? `<div class="exercise__heading">${escapeHtml(exercise.heading)}</div>` : ""}
            ${exercise.image ? `<div class="exercise__image-wrapper"><img class="exercise__image" src="${escapeHtml(exercise.image)}" alt=""></div>` : ""}
            ${exercise.instructions ? `<div class="exercise__instructions">${escapeHtml(exercise.instructions)}</div>` : ""}
            ${questions}
          </div>
          <div class="modal-footer">
            <div class="exercise__answer-status">
              <span class="exercise__answer-correct"><span class="bi bi-check-lg text-success"></span> Correct!</span>
              <span class="exercise__answer-incorrect"><span class="bi bi-x-lg text-danger"></span> Try Again!</span>
            </div>
            <button type="button" class="btn btn-primary modal-submit">Submit</button>
          </div>
        </div></div>
      </div>
    `
    this.$exercise = $(this.element).find("#exercise")
    this.$exercise.on("click", ".modal-submit", (event) => this.submit(event))
    return this
  }

  show() {
    this.modal = Modal.getOrCreateInstance(this.$exercise[0])
    this.modal.show()
    this.isOpen = true
    this.$exercise.one("hidden.bs.modal", () => {
      this.isOpen = false
      this.emit("hidden", this.id)
    })
    this.initDragDrop()
  }

  hide() {
    this.modal?.hide()
  }

  remove() {
    this.sortables?.forEach((sortable) => sortable.destroy())
    this.$exercise?.off()
    this.element.remove()
    this.off()
  }

  initDragDrop() {
    if (this.sortables?.length) return
    const groupName = `exercise-${this.id}`
    this.sortables = []
    this.element.querySelectorAll(".draggable").forEach((source) => {
      this.sortables.push(Sortable.create(source, {
        group: { name: groupName, pull: "clone", put: true },
        sort: false,
        animation: 140,
        ghostClass: "sortable-ghost",
        dragClass: "sortable-drag",
        onAdd: (event) => event.item.remove()
      }))
    })
    this.element.querySelectorAll(".droppable").forEach((target) => {
      this.sortables.push(Sortable.create(target, {
        group: { name: groupName, pull: true, put: true },
        animation: 140,
        ghostClass: "sortable-ghost",
        dragClass: "sortable-drag",
        onAdd: (event) => {
          Array.from(event.to.children).forEach((child) => {
            if (child !== event.item) child.remove()
          })
        }
      }))
    })
  }

  submit(event) {
    event.currentTarget.blur()
    const correct = JSON.stringify(this.submittedAnswers()) === JSON.stringify(this.correctAnswers())
    const status = this.$exercise.find(".exercise__answer-status")
    status.removeClass("correct incorrect")
    Utils.reflow(status[0])
    status.addClass(correct ? "correct" : "incorrect")
    this.emit(correct ? "answer:correct" : "answer:incorrect", this.id)
    window.setTimeout(() => status.removeClass("correct incorrect"), 2000)
  }

  submittedAnswers() {
    return this.$exercise.find(".exercise__question").toArray().map((question, index) => {
      const selector = `[name='q${index + 1}-choices']`
      if (this.$exercise.find(selector).length) return this.$exercise.find(`${selector}:checked`).val()
      let answer = $(question).find("[data-cid]").toArray().map((element) => $(element).data("cid"))
      if (answer.length < 2) return answer[0]
      if ($(question).find(".sort-answers").length) answer = answer.sort()
      return answer
    })
  }

  correctAnswers() {
    return (this.data?.questions || []).map((question) => question.answer)
  }

  formattedData() {
    if (!this.data?.questions) return this.data
    const formatted = Utils.clone(this.data)
    formatted.wordBankIndex = this.wordBankIndex()
    formatted.questions.forEach((question) => {
      let text = question.text || ""
      const multi = text.match(/{(\d+)\?}/)
      if (multi) text = text.replace(multi[0], `<div class="sort-answers">${Utils.repeat("{?}", Number(multi[1]))}</div>`)
      question.text = text.replace(/{\?}/g, '<span class="droppable exercise__placeholder"></span>')
    })
    return formatted
  }

  wordBankIndex() {
    const questions = this.data?.questions || []
    const indexes = questions.map((question, index) => question.type === "fill-in-the-blank" ? index : -1).filter((index) => index >= 0)
    return indexes.length ? indexes[indexes.length - 1] : -1
  }
}

class VideoLessonView extends Widget {
  constructor(options = {}) {
    const playerElement = document.getElementById(options.playerId)
    super(playerElement?.closest("#video-lessons-container") || document.body)
    this.completedLessonIds = options.completedLessonIds || []
    this.exercises = options.exercises ? JSON.parse(Utils.decode(options.exercises)) : {}
    this.lessonTable = this.$("table.video-lessons")
    this.isPreview = this.lessonTable.hasClass("preview")
    this.player = videojs(options.playerId, { controls: true, fluid: true, ...(options.playerOptions || {}) })
    $(`#${options.playerId}`).on("contextmenu", () => false)
    this.delegate("click", ".video-lesson-link", this.loadLesson)
    this.delegate("click", ".objective-link", this.loadObjective)
    this.delegate("click", ".video-lesson-exercise-link", this.loadExercise)
    this.delegate("click", "#video-container", this.enterFullscreen)
    this.delegate("click", "#complete-lessons", this.completeLessons)
    this.updateLessonTable()
  }

  updateLessonTable(completedVideoLesson) {
    if (this.isPreview) return
    if (this.exerciseView?.isOpen) {
      this.exerciseView.on("hidden", () => this.updateLessonTable(completedVideoLesson))
      return
    }
    if (completedVideoLesson) this.completedLessonIds.push(completedVideoLesson.get("video_lesson_id"))
    this.completedLessonIds.forEach((id) => {
      this.lessonTable.find(`tr[data-video-lesson-id='${id}']`).removeClass("default current").addClass("complete")
    })
    const current = this.lessonTable.find("tr.complete").length
      ? this.lessonTable.find("tr.complete").last().next("tr")
      : this.lessonTable.find("tr[data-video-lesson-id]").first()
    if (current.length) {
      current.removeClass("default").addClass("current")
      this.loadLesson(null, current[0])
      this.loadObjective(null, current[0])
    } else if (completedVideoLesson) {
      this.$("#complete-lessons").show()
    }
  }

  loadLesson(event, target) {
    event?.preventDefault()
    const row = $(target || this.lessonTable.find("tr.current")).closest("tr")
    this.lessonTable.find("tr").removeClass("loaded")
    row.addClass("loaded")
    const lessonId = row.data("video-lesson-id")
    const fileType = row.data("video-lesson-file-type")
    if (!document.querySelector(".separate-video")) {
      this.$(`#video-${this.lessonTable.data("rubric-id")}`).show()
      this.player.src({ src: this.videoPath(lessonId), type: fileType })
    } else {
      const rubricId = row.data("rubrics-id")
      this.$(`#video-${rubricId}`).show()
      this.player.src({ src: this.customVideoPath(lessonId, rubricId), type: fileType })
    }
  }

  loadObjective(event, target) {
    event?.preventDefault()
    const row = $(target || this.lessonTable.find("tr.current")).closest("tr")
    const rubricId = row.data("rubrics-id")
    this.$(`#objective-${rubricId}`).html(row.data("video-lesson-objective")).show()
  }

  loadExercise(event, target) {
    event.preventDefault()
    this.exerciseView?.remove()
    const lessonId = $(target).closest("tr").data("video-lesson-id")
    this.exerciseView = new ExerciseView({ id: lessonId, data: this.exercises[lessonId] }).render()
    this.$("#video-lesson-exercise").empty().append(this.exerciseView.element)
    this.exerciseView.on("answer:correct", this.completeLesson, this)
    this.exerciseView.show()
  }

  completeLesson(lessonId) {
    if (this.exerciseView?.isOpen) window.setTimeout(() => this.exerciseView.hide(), 2000)
    if (this.isPreview) return
    const completed = new CompletedVideoLesson()
    completed.save({ video_lesson_id: lessonId, assignment_id: this.parseAssignmentId() }, {
      success: () => this.updateLessonTable(completed)
    }).catch(() => window.alert("Could not save lesson completion. Please try again."))
  }

  completeLessons() {
    Utils.reloadPage()
  }

  parseAssignmentId() {
    const match = Utils.getPathname().match(/^\/assignments\/(\d+)/)
    return match ? Number(match[1]) : undefined
  }

  enterFullscreen() {
    if (!Utils.isFullscreen() && !this.$(".vjs-playing").length) this.player.requestFullscreen()
  }

  videoPath(id) {
    return `${Utils.getPathname()}/video_lessons/${id}?rid=${Utils.generateToken()}`
  }

  customVideoPath(id, rubricId) {
    return `/genres/${rubricId}/video_lessons/${id}?rid=${Utils.generateToken()}`
  }
}

Object.assign(window, { VideoUploadView, ExerciseFormView, VideoLessonView })
